import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { environment } from 'spotlight-ui/environments/environment';
import { z } from 'zod';
import { TemplateDetails } from '../../models/engage/template-details.model';
import moment from 'moment-timezone';

export interface TemplateSource {
    getTemplateDetails: (locationNumber: string, templateID: string) => Observable<TemplateDetails>
}

export class DigitalMarketingTemplateSource implements TemplateSource {
  constructor(
        private readonly httpClient: HttpClient
  ) {
  }

  getTemplateDetails(locationNumber: string, templateID: string): Observable<TemplateDetails> {
    const headers = new HttpHeaders({
      'Requested-Localities': locationNumber
    });
    const templateDetails$ = new Subject<TemplateDetails>();

    this.httpClient
      .get<TemplateDetails>(`${environment.digitalMarketingCampaignsServiceURL}/api/restaurant/templates/${templateID}`, { headers })
      .subscribe({
        next: (response) => {
          const result = TemplateDetailsSchema.safeParse(response);
          if (!result.success) {
            templateDetails$.error(new Error(`Invalid response format for template ${templateID}`));
            return;
          }
          const templateWithDateRestriction = {
            ...result.data,
            endDateRestriction: result.data.endDateRestriction
              ? moment(result.data.endDateRestriction)
              : moment().add(365, 'day')
          };

          templateDetails$.next(templateWithDateRestriction as TemplateDetails);
          templateDetails$.complete();
        },
        error: (err) => {
          templateDetails$.error(err.error?.message );
        }
      });

    return templateDetails$.asObservable();
  }

}

export const ThemeSchema = z.object({
  id: z.string(),
  title: z.string(),
  tags: z.array(z.string()),
  preHeader: z.string(),
  headerImage: z.object({
    desktopURL: z.string(),
    mobileURL: z.string(),
  }),
  subject: z.string(),
  emailBody: z.string(),
  appMessage: z.string().optional(),
  heroImage: z.object({
    desktopURL: z.string(),
    mobileURL: z.string(),
  }).optional(),
  ctaButtonImage: z.object({
    desktopURL: z.string(),
    mobileURL: z.string(),
  }).optional(),
});

export const NotificationLayoutSchema = z.object({
  id: z.string(),
  html: z.string(),
  themes: z.array(ThemeSchema)
});

export const TemplateDetailsSchema = z.object({
  id: z.string(),
  campaignType: z.string(),
  category: z.string(),
  title: z.string(),
  rewardId: z.string().optional(),
  rewardValidDetails: z.string().optional(),
  description: z.string().optional(),
  notificationLayouts: z.array(NotificationLayoutSchema).optional(),
  endDateRestriction: z.string().datetime().optional(),
  themes: z.array(ThemeSchema),
  heroImages: z.array(z.object({
    desktopURL: z.string(),
    mobileURL: z.string(),
  })).optional()
});